"use client";

import { useEffect, useState } from "react";

// Purely decorative texture behind a form -- one is picked at random per page load, so repeat
// visitors see some variety rather than the same one every time. Never the source of any real
// content, hence the empty alt/aria-hidden below.
const FORM_BACKGROUNDS = [
  "/images/decor/yoga-poses-1.webp",
  "/images/decor/yoga-poses-2.webp",
  "/images/decor/yoga-poses-3.webp",
];

// Renders nothing itself but two absolutely-positioned layers -- the caller must be `relative
// overflow-hidden` for these to clip to its bounds. Shared by FormSplitLayout (customer-facing
// forms) and AdminSignIn (the one internal-tool form with no split-image layout of its own).
export default function DecorativeFormBackground() {
  // Picked client-side only, after mount -- picking it during the initial render would make the
  // server-rendered HTML and the client's first render disagree (nothing about Math.random()
  // lets the two agree), tripping a hydration mismatch. Starting blank for the first paint is a
  // fine trade for a purely decorative image with no layout weight of its own.
  const [bg, setBg] = useState<string | null>(null);
  useEffect(() => {
    setBg(FORM_BACKGROUNDS[Math.floor(Math.random() * FORM_BACKGROUNDS.length)]);
  }, []);

  if (!bg) return null;

  return (
    <>
      {/* A small, repeating tile rather than one image stretched/cropped to cover the whole
          area (object-cover on art with several small figures in it was zooming into one
          cropped corner, reading as distorted) -- a fixed tile width keeps every figure at a
          legible, undistorted scale regardless of how tall or short the container ends up. */}
      <div
        aria-hidden
        className="absolute inset-0 bg-repeat"
        style={{ backgroundImage: `url(${bg})`, backgroundSize: "200px auto" }}
      />
      {/* Heavy white tint -- the illustration is meant to read as a faint texture, not compete
          with the form sitting on top of it for attention or legibility. */}
      <div className="absolute inset-0 bg-white/85" />
    </>
  );
}
